import { Component, type ErrorInfo, type ReactNode } from "react";
import { createId } from "../app/runtimeContext";
import { normalizeError } from "./ApplicationError";
import type { ObservabilityClient } from "../observability/types";

type ErrorBoundaryProps = {
  children: ReactNode;
  observability: ObservabilityClient;
  boundary?: "global" | "route" | "runtime";
};

type ErrorBoundaryState = {
  errorId: string | null;
  safeMessage: string | null;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    errorId: null,
    safeMessage: null,
  };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const normalized = normalizeError(error, { boundary: "global" });

    return {
      errorId: createId("incident"),
      safeMessage: normalized.safeMessage,
    };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    const normalized = normalizeError(error, {
      boundary: "global",
      componentStack: errorInfo.componentStack,
    });

    void this.props.observability.error({
      category: "runtime",
      eventName: "react_error_boundary",
      message: "React render-time error captured by global boundary.",
      error: normalized,
      context: {
        incidentId: this.state.errorId,
      },
    });
  }

  render() {
    if (this.state.errorId) {
      return (
        <main className="login-shell" role="alert">
          <section className="login-card">
            <div className="brand-mark">FC</div>
            <p className="eyebrow">Runtime incident</p>
            <h1>SS Core recovered from a runtime failure</h1>
            <p className="login-copy">
              {this.state.safeMessage ?? "An unexpected error occurred."}
            </p>
            <p className="error-text">Incident ID: {this.state.errorId}</p>
            <button
              className="primary-action"
              type="button"
              onClick={() => window.location.reload()}
            >
              Reload SS Core
            </button>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}
